import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link2, ShieldCheck, ShieldAlert, CreditCard, Download, Loader2, CheckCircle2, Copy, ExternalLink, Info } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { AltchaWidget } from '@/components/AltchaWidget';
import { toast } from 'sonner';
import type { TierType } from '@/components/portal/TierSelection';
import {
  getHostedLifecycleMeta,
  HOSTED_LIFECYCLE_STEPS,
  resolveHostedAutomation,
  resolveHostedPluginInstall,
  resolveHostedLifecycle,
  SHADOWSCAN_WORDPRESS_PLUGIN,
  type HostedLifecycleManagedSite,
} from '@/lib/wordpressLifecycle';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const getPairingCode = (pairingLink?: string) => {
  if (!pairingLink) return null;
  try {
    const url = new URL(pairingLink);
    return url.searchParams.get('pairing_code');
  } catch {
    const match = pairingLink.match(/pairing_code=([^&]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  }
};

const resolveDomainOrigin = (domainName?: string | null) => {
  const raw = (domainName || '').trim();
  if (!raw) return null;

  const withScheme = raw.includes('://') ? raw : `https://${raw}`;
  try {
    const url = new URL(withScheme);
    if (!url.hostname) return null;
    return url.origin;
  } catch {
    // Fall through to relaxed extraction.
  }

  const hostCandidate = raw
    .replace(/^[a-z]+:\/\//i, '')
    .replace(/^\/+|\/+$/g, '')
    .split('/')[0];
  if (!hostCandidate) return null;

  try {
    const url = new URL(`https://${hostCandidate}`);
    if (!url.hostname) return null;
    return url.origin;
  } catch {
    return null;
  }
};

const buildWpAdminPairingUrl = (domainName?: string | null, wpAdminUrl?: string | null, pairingCode?: string | null) => {
  if (!pairingCode) return null;
  const adminBaseUrl = toWpAdminBaseUrl(domainName, wpAdminUrl);
  if (adminBaseUrl) {
    const url = new URL(adminBaseUrl.toString());
    url.pathname = `${adminBaseUrl.pathname}admin.php`;
    url.searchParams.set('page', 'shadowscan');
    url.searchParams.set('pairing_code', pairingCode);
    return url.toString();
  }
  if (!domainName) return null;
  try {
    const origin = resolveDomainOrigin(domainName);
    if (!origin) return null;
    const url = new URL(origin);
    return `${url.origin}/wp-admin/admin.php?page=shadowscan&pairing_code=${encodeURIComponent(pairingCode)}`;
  } catch {
    return null;
  }
};

const buildWpAdminUrlFromInput = (domainName?: string | null, rawValue?: string | null) => {
  const value = (rawValue || '').trim();
  if (!value) return null;
  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value;
  }
  const origin = resolveDomainOrigin(domainName);
  if (!origin) return null;
  try {
    const path = value.startsWith('/') ? value : `/${value}`;
    return `${origin}${path}`;
  } catch {
    return null;
  }
};

const toWpAdminBaseUrl = (domainName?: string | null, rawValue?: string | null) => {
  const normalized = buildWpAdminUrlFromInput(domainName, rawValue);
  if (!normalized) return null;

  try {
    const url = new URL(normalized);
    let path = url.pathname || '/';

    // If users provide wp-login, route installer links through wp-admin.
    if (/\/wp-login(?:\.php)?\/?$/i.test(path)) {
      path = '/wp-admin/';
    } else if (path.endsWith('.php')) {
      const slashIndex = path.lastIndexOf('/');
      path = slashIndex >= 0 ? path.slice(0, slashIndex + 1) : '/';
    } else if (!path.endsWith('/')) {
      path += '/';
    }

    if (path === '/') {
      path = '/wp-admin/';
    }

    url.pathname = path;
    url.search = '';
    url.hash = '';
    return url;
  } catch {
    return null;
  }
};

const buildWpAdminPluginSearchUrl = (
  domainName?: string | null,
  wpAdminUrl?: string | null,
  wpAdminInput?: string,
) => {
  const adminBaseUrl =
    toWpAdminBaseUrl(domainName, wpAdminUrl) ||
    toWpAdminBaseUrl(domainName, wpAdminInput) ||
    toWpAdminBaseUrl(domainName, '/wp-admin/');
  if (!adminBaseUrl) return null;

  const installUrl = new URL(adminBaseUrl.toString());
  installUrl.pathname = `${adminBaseUrl.pathname}plugin-install.php`;
  installUrl.searchParams.set('s', SHADOWSCAN_WORDPRESS_PLUGIN.slug);
  installUrl.searchParams.set('tab', 'search');
  installUrl.searchParams.set('type', 'term');
  return installUrl.toString();
};

type SecureWebsiteOnboardingDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  domain?: {
    id: string;
    domain_name: string;
    wp_admin_url?: string | null;
  } | null;
  managedSite?: HostedLifecycleManagedSite | null;
  pairingLink?: string;
  
  paymentNeeded: boolean;
  stepsComplete?: boolean;
  onGeneratePairingLink?: (altchaToken: string) => void;
  onResumePayment?: () => void;
  paymentActionLabel?: string;
  onCheckStatus?: () => void;
  onWpAdminUrlUpdated?: (domainId: string, wpAdminUrl: string) => void;
  selectedTier?: TierType;
};

type OnboardingStep = {
  id: string;
  title: string;
  description: string;
  action?: ReactNode;
  detail?: ReactNode;
};

export function SecureWebsiteOnboardingDialog({
  open,
  onOpenChange,
  domain,
  managedSite,
  pairingLink,
  paymentNeeded,
  stepsComplete = false,
  onGeneratePairingLink,
  onResumePayment,
  paymentActionLabel = 'Complete billing',
  onCheckStatus,
  onWpAdminUrlUpdated,
  selectedTier,
}: SecureWebsiteOnboardingDialogProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const [wpAdminInput, setWpAdminInput] = useState('');
  const [wpAdminChoice, setWpAdminChoice] = useState<'yes' | 'no'>('yes');
  const [savingWpAdmin, setSavingWpAdmin] = useState(false);
  const [pairingAltchaToken, setPairingAltchaToken] = useState<string | null>(null);
  const [confirmingMigration, setConfirmingMigration] = useState(false);
  const isBasicPlan = selectedTier === 'basic';
  const monitoredPlanLabel = selectedTier === 'premium' ? 'monitored response' : 'monitored security';
  const lifecycle = resolveHostedLifecycle(domain, managedSite);
  const lifecycleMeta = getHostedLifecycleMeta(lifecycle.state);
  const installTracking = resolveHostedPluginInstall(managedSite);
  const automationTracking = resolveHostedAutomation(managedSite);
  const migrationPending = Boolean(managedSite?.hosted_is_migration && !managedSite?.hosted_migration_confirmed_at);
  const migrationConfirmed = Boolean(managedSite?.hosted_is_migration && managedSite?.hosted_migration_confirmed_at);
  const showHostedLifecycle = lifecycle.isHosted;

  useEffect(() => {
    if (open) {
      setStepIndex(0);
      setPairingAltchaToken(null);
    }
  }, [open, domain?.id]);

  useEffect(() => {
    if (pairingLink) {
      setPairingAltchaToken(null);
    }
  }, [pairingLink]);

  useEffect(() => {
    if (!open) return;
    setWpAdminInput(domain?.wp_admin_url || '');
    setWpAdminChoice(domain?.wp_admin_url ? 'no' : 'yes');
  }, [open, domain?.wp_admin_url, domain?.id]);

  const handleSaveWpAdminUrl = async (overrideValue?: string) => {
    if (!domain?.id) {
      toast.error('Domain not found.');
      return;
    }
    const normalized = buildWpAdminUrlFromInput(domain?.domain_name, overrideValue ?? wpAdminInput);
    if (!normalized) {
      toast.error('Enter your full WordPress admin URL.');
      return;
    }
    try {
      const url = new URL(normalized);
      if (!['http:', 'https:'].includes(url.protocol)) {
        throw new Error('Invalid protocol');
      }
    } catch {
      toast.error('Please enter a valid URL (including https://).');
      return;
    }

    setSavingWpAdmin(true);
    try {
      const { data, error } = await supabase.functions.invoke('update-domain-credentials', {
        body: {
          domainId: domain.id,
          wpAdminUrl: normalized,
        },
      });
      if (error || data?.error) throw new Error(error?.message || data?.error || 'Unable to update admin URL');
      toast.success('WP Admin URL saved.');
      onWpAdminUrlUpdated?.(domain.id, normalized);
    } catch (err: unknown) {
      console.error('update-domain-credentials failed:', err);
      const message = err instanceof Error ? err.message : 'Unable to save admin URL.';
      toast.error(message);
    } finally {
      setSavingWpAdmin(false);
    }
  };

  const handleConfirmHostedMigration = async () => {
    if (!domain?.id) {
      toast.error('Domain not found.');
      return;
    }

    setConfirmingMigration(true);
    try {
      const { data, error } = await supabase.functions.invoke('confirm-hosted-migration', {
        body: { domainId: domain.id },
      });
      if (error || data?.error) {
        throw new Error(error?.message || data?.error || 'Unable to confirm hosted migration.');
      }
      toast.success(data?.message || 'Hosted migration confirmed. Plugin setup is resuming.');
      onCheckStatus?.();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unable to confirm hosted migration.';
      toast.error(message);
    } finally {
      setConfirmingMigration(false);
    }
  };

  const steps = useMemo<OnboardingStep[]>(() => {
    if (migrationPending) {
      return [
        {
          id: 'migration-pending',
          title: 'Migration pending',
          description: 'Billing and hosting provisioning can continue, but protection setup is paused until your migrated WordPress site is live on the hosted account.',
          detail: (
            <div className="rounded-md border border-border/50 bg-muted/40 p-3 space-y-2 text-xs text-muted-foreground">
              <p className="text-foreground font-medium">Before plugin setup resumes:</p>
              <p>1. Migrate the existing WordPress files and database to the hosted account.</p>
              <p>2. Update DNS or domain routing so the site loads from the hosted account.</p>
              <p>3. Confirm the live site loads correctly on the hosted account.</p>
              <p>4. Click “Migration complete - run plugin setup”.</p>
              <p>Protection is not live yet while this step is pending.</p>
            </div>
          ),
          action: (
            <Button variant="outline" size="sm" onClick={handleConfirmHostedMigration} disabled={confirmingMigration}>
              {confirmingMigration ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              Migration complete - run plugin setup
            </Button>
          ),
        },
        {
          id: 'after-confirmation',
          title: 'What happens next',
          description: 'After confirmation, the hosted workflow resumes and attempts plugin setup, pairing, and later heartbeat verification.',
          detail: (
            <div className="space-y-2 text-xs text-muted-foreground">
              <p>Automation status: {automationTracking.label}</p>
              <p>{automationTracking.description}</p>
              {onCheckStatus && (
                <Button variant="outline" size="sm" onClick={onCheckStatus}>
                  Check status
                </Button>
              )}
            </div>
          ),
        },
      ];
    }

    if (migrationConfirmed) {
      return [
        {
          id: 'migration-confirmed',
          title: 'Migration confirmed',
          description: 'Hosted plugin setup has resumed after your migration confirmation.',
          detail: (
            <div className="rounded-md border border-border/50 bg-muted/40 p-3 space-y-2 text-xs text-muted-foreground">
              <p>Current automation status: {automationTracking.label}</p>
              <p>{automationTracking.description}</p>
              {managedSite?.hosted_migration_confirmed_at && (
                <p>Confirmed at: {new Date(managedSite.hosted_migration_confirmed_at).toLocaleString()}</p>
              )}
            </div>
          ),
          action: onCheckStatus ? (
            <Button variant="outline" size="sm" onClick={onCheckStatus}>
              Check status
            </Button>
          ) : undefined,
        },
      ];
    }

    const domainLabel = domain?.domain_name ? ` for ${domain.domain_name}` : '';
    const pairingCode = getPairingCode(pairingLink) || pairingLink || '';
    const storedWpAdminUrl = buildWpAdminUrlFromInput(domain?.domain_name, domain?.wp_admin_url);
    const defaultWpAdminUrl = buildWpAdminUrlFromInput(domain?.domain_name, '/wp-admin/');
    const manualWpAdminUrl = buildWpAdminUrlFromInput(domain?.domain_name, wpAdminInput);
    const resolvedWpAdminUrl =
      wpAdminChoice === 'no'
        ? manualWpAdminUrl || storedWpAdminUrl || defaultWpAdminUrl
        : storedWpAdminUrl || defaultWpAdminUrl;
    const wpAdminUrl = buildWpAdminPairingUrl(
      domain?.domain_name,
      resolvedWpAdminUrl,
      pairingCode,
    );
    const pluginSearchUrl = buildWpAdminPluginSearchUrl(
      domain?.domain_name,
      resolvedWpAdminUrl,
      wpAdminChoice === 'no' ? wpAdminInput : undefined,
    );
    const hasStoredWpAdminUrl = Boolean(domain?.wp_admin_url);
    const adminUrlForm = (
      <div className="rounded-md border border-border/50 bg-muted/40 p-3 space-y-2">
        <div className="flex items-center gap-2">
          <Label className="text-xs">Is your admin URL /wp-admin/ ?</Label>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-border/60 text-muted-foreground">
                <Info className="h-3 w-3" />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              This helps the Connector plugin open the correct admin page to finish security setup.
            </TooltipContent>
          </Tooltip>
        </div>
        <RadioGroup value={wpAdminChoice} onValueChange={(value) => setWpAdminChoice(value as 'yes' | 'no')}>
          <div className="flex items-center gap-2">
            <RadioGroupItem id="wp-admin-yes" value="yes" />
            <Label htmlFor="wp-admin-yes" className="text-sm">Yes</Label>
          </div>
          <div className="flex items-center gap-2">
            <RadioGroupItem id="wp-admin-no" value="no" />
            <Label htmlFor="wp-admin-no" className="text-sm">No</Label>
          </div>
        </RadioGroup>
        {wpAdminChoice === 'yes' && (
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setWpAdminChoice('no')}
            >
              My admin URL changed
            </Button>
            {hasStoredWpAdminUrl && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSaveWpAdminUrl('/wp-admin/')}
                disabled={savingWpAdmin}
              >
                Use /wp-admin/ instead
              </Button>
            )}
          </div>
        )}
        {wpAdminChoice === 'no' && (
          <>
            <Label className="text-xs">Custom WP Admin URL (optional)</Label>
            <Input
              value={wpAdminInput}
              onChange={(event) => setWpAdminInput(event.target.value)}
              placeholder="/wp-login or https://yoursite.com/secret-admin/"
              disabled={savingWpAdmin}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSaveWpAdminUrl()}
                disabled={savingWpAdmin || !wpAdminInput.trim()}
              >
                {savingWpAdmin ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <ExternalLink className="h-4 w-4 mr-2" />
                )}
                Save admin URL
              </Button>
              {hasStoredWpAdminUrl && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleSaveWpAdminUrl('/wp-admin/')}
                  disabled={savingWpAdmin}
                >
                  Use /wp-admin/ instead
                </Button>
              )}
              {hasStoredWpAdminUrl && (
                <span className="text-xs text-muted-foreground break-all">
                  Current: {domain?.wp_admin_url}
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              If your WordPress admin login is custom, save it here so the pairing link opens the correct page.
            </p>
          </>
        )}
      </div>
    );
    return [
      {
        id: 'before-we-begin',
        title: 'Before we begin',
        description: 'Confirm your WordPress admin URL so we can link you directly to the right pages.',
        detail: adminUrlForm,
      },
      {
        id: 'install',
        title: `Step 1: Install ${SHADOWSCAN_WORDPRESS_PLUGIN.name}`,
        description: `Install the official ${SHADOWSCAN_WORDPRESS_PLUGIN.name} plugin on your WordPress site${domainLabel}.`,
        action: (
          <div className="flex flex-wrap items-center gap-2">
            {pluginSearchUrl ? (
              <Button asChild variant="outline" size="sm">
                <a href={pluginSearchUrl} target="_blank" rel="noreferrer">
                  <Download className="h-4 w-4 mr-2" />
                  Search in WP Admin
                </a>
              </Button>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => toast.error('Unable to resolve WP Admin URL. Choose "No" and enter your admin URL first.')}
              >
                <Download className="h-4 w-4 mr-2" />
                Search in WP Admin
              </Button>
            )}
            <Button asChild variant="ghost" size="sm">
              <a href={SHADOWSCAN_WORDPRESS_PLUGIN.wordpressOrgUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                WordPress.org listing
              </a>
            </Button>
          </div>
        ),
        detail: (
          <div className="space-y-2 text-xs text-muted-foreground">
            <p>
              Search for <span className="font-mono text-foreground">{SHADOWSCAN_WORDPRESS_PLUGIN.slug}</span> in Plugins → Add New, or download it from the WordPress.org directory. Activate the plugin after installing.
            </p>
            {managedSite?.can_install_plugins === false && (
              <p className="text-amber-400">
                Plugin installs from WP Admin appear to be restricted on this site. Use your host or deployment workflow to add the plugin, then return here to pair it.
              </p>
            )}
          </div>
        ),
      },
      {
        id: 'pair',
        title: 'Step 2: Pair the plugin',
        description: `Generate a short-lived pairing code, then enter it in ${SHADOWSCAN_WORDPRESS_PLUGIN.name} to link this website to your portal.`,
        action: (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (!pairingAltchaToken || !onGeneratePairingLink) {
                return;
              }
              onGeneratePairingLink(pairingAltchaToken);
            }}
            disabled={!onGeneratePairingLink || !pairingAltchaToken}
            title="Expires in 10 minutes. This code links your WordPress plugin to this ShadowScan site entry."
          >
            <Link2 className="h-4 w-4 mr-2" />
            Generate pairing code
          </Button>
        ),
        detail: (
          <div className="space-y-2">
            <div className="rounded-md border border-border/50 bg-muted/40 p-3 space-y-2">
              <p className="text-xs text-muted-foreground">
                Complete verification before generating a pairing code.
              </p>
              <AltchaWidget onToken={setPairingAltchaToken} />
              <p className="text-xs text-muted-foreground">
                Status: {pairingAltchaToken ? 'Verified' : 'Pending verification'}
              </p>
            </div>
            {pairingLink ? (
              <>
                <p className="text-xs text-emerald-400 break-all">
                  Pairing code: {pairingCode}
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(pairingCode);
                        toast.success('Pairing code copied to clipboard.');
                      } catch {
                        const textarea = document.createElement('textarea');
                        textarea.value = pairingCode;
                        textarea.style.position = 'fixed';
                        textarea.style.left = '-9999px';
                        document.body.appendChild(textarea);
                        textarea.focus();
                        textarea.select();
                        try {
                          document.execCommand('copy');
                          toast.success('Pairing code copied to clipboard.');
                        } catch {
                          toast.error('Unable to copy pairing code. Select and copy manually.');
                        } finally {
                          textarea.remove();
                        }
                      }
                    }}
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Copy code
                  </Button>
                  {wpAdminUrl && (
                    <Button asChild variant="outline" size="sm">
                      <a href={wpAdminUrl} target="_blank" rel="noreferrer">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Open WP Admin
                      </a>
                    </Button>
                  )}
                </div>
                {paymentNeeded && (
                  <p className="text-xs text-amber-400">
                    Pairing is ready, but protection is only marked live once billing is active and the plugin starts sending heartbeats.
                  </p>
                )}
              </>
            ) : (
              <p className="text-xs text-muted-foreground">Codes expire after 10 minutes.</p>
            )}
          </div>
        ),
      },
      {
        id: 'complete',
        title: paymentNeeded ? `Step 3: All done - activate ${isBasicPlan ? 'hosting setup' : 'monitoring'}` : 'Step 3: Bring guards live',
        description: paymentNeeded
          ? `Your setup is complete. ${isBasicPlan ? 'Hosting setup' : 'Monitoring'} begins as soon as billing is confirmed.`
          : isBasicPlan
            ? 'Your setup is complete. We are verifying the connector and syncing hosted onboarding status.'
            : 'Your setup is complete. We are verifying the connector and starting monitored coverage.',
        action: paymentNeeded ? (
          <Button variant="outline" size="sm" onClick={onResumePayment} disabled={!onResumePayment}>
            <CreditCard className="h-4 w-4 mr-2" />
            {paymentActionLabel}
          </Button>
        ) : (
          <div className="inline-flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Checking status… this can take a minute.
          </div>
        ),
        detail: paymentNeeded ? (
          <div className="inline-flex items-center text-xs text-amber-400">
            <ShieldAlert className="h-4 w-4 mr-2" />
            Once paid, {isBasicPlan ? 'hosting onboarding activates automatically.' : 'monitored coverage activates automatically.'}
          </div>
        ) : (
          <div className="space-y-2 text-xs text-muted-foreground">
            <p>
              {isBasicPlan
                ? 'Hosted onboarding status and ShadowScan Guard pairing will continue syncing here after the first verified heartbeat from the plugin.'
                : 'We’ll surface validated activity and plan-aligned next steps here after the first verified heartbeat from the plugin.'}
            </p>
            {onCheckStatus && (
              <Button variant="outline" size="sm" onClick={onCheckStatus}>
                Check status
              </Button>
            )}
          </div>
        ),
      },
    ];
  }, [
    domain?.domain_name,
    domain?.wp_admin_url,
    onGeneratePairingLink,
    paymentActionLabel,
    onResumePayment,
    pairingLink,
    paymentNeeded,
    onCheckStatus,
    handleSaveWpAdminUrl,
    wpAdminInput,
    savingWpAdmin,
    wpAdminChoice,
    pairingAltchaToken,
    handleConfirmHostedMigration,
    migrationPending,
    migrationConfirmed,
    automationTracking.description,
    automationTracking.label,
    managedSite?.hosted_migration_confirmed_at,
    confirmingMigration,
  ]);

  const currentStep = steps[stepIndex];
  const progress = stepIndex === 0 ? 0 : Math.round((stepIndex / (steps.length - 1)) * 100);

  if (stepsComplete) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="cyber-card max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-xl font-mono">You’re all set</DialogTitle>
            <DialogDescription>
              {isBasicPlan ? 'Welcome to Basic Hosting with ShadowScan Guard.' : 'Welcome to ShadowScan monitored security.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="inline-flex items-center gap-2 text-sm text-emerald-400">
              <CheckCircle2 className="h-4 w-4" />
              {isBasicPlan ? 'Connected and ready for hosted onboarding.' : 'Connected and monitored.'}
            </div>
            <p className="text-sm text-muted-foreground">
              {isBasicPlan
                ? `That’s all you need to do. ${SHADOWSCAN_WORDPRESS_PLUGIN.name} is connected and hosted onboarding status will keep syncing in ShadowScan. Managed security features still require Essential or Premium.`
                : `That’s all you need to do. ${SHADOWSCAN_WORDPRESS_PLUGIN.name} is connected and ${monitoredPlanLabel} will continue from here. If anything needs attention, ShadowScan will surface the next steps in the portal and reach out when required.`}
            </p>
          </div>
          <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button size="sm" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="cyber-card max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-mono">Secure Website Setup</DialogTitle>
          <DialogDescription>
            Step {stepIndex === 0 ? 'Intro' : `${stepIndex} of ${steps.length - 1}`}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {showHostedLifecycle && (
            <div className="rounded-md border border-border/60 bg-muted/20 p-4 space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="space-y-1">
                  <div className="text-sm font-semibold text-foreground">Hosting setup progress</div>
                  <p className="text-xs text-muted-foreground">{lifecycleMeta.description}</p>
                </div>
                <span className="rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-xs text-foreground">
                  {lifecycleMeta.label}
                </span>
              </div>
              <div className="grid gap-2 sm:grid-cols-5">
                {HOSTED_LIFECYCLE_STEPS.map((step, index) => {
                  const isActive = index <= lifecycle.stateIndex;
                  return (
                    <div
                      key={step.state}
                      className={`min-h-16 rounded-md border px-3 py-2 text-xs ${
                        isActive ? 'border-primary/40 bg-primary/10 text-foreground' : 'border-border/50 bg-background/40 text-muted-foreground'
                      }`}
                    >
                      <div className="font-medium leading-snug">{step.label}</div>
                    </div>
                  );
                })}
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                <div className="rounded-md border border-border/50 bg-background/40 px-3 py-2 text-xs text-muted-foreground">
                  <div className="mb-1">Plugin</div>
                  <div className="text-foreground">{lifecycle.pluginInstalled ? 'Detected' : 'Not yet detected'}</div>
                </div>
                <div className="rounded-md border border-border/50 bg-background/40 px-3 py-2 text-xs text-muted-foreground">
                  <div className="mb-1">Pairing</div>
                  <div className="text-foreground">{lifecycle.pluginPaired ? 'Connected' : 'Not yet paired'}</div>
                </div>
                <div className="rounded-md border border-border/50 bg-background/40 px-3 py-2 text-xs text-muted-foreground">
                  <div className="mb-1">Protection sync</div>
                  <div className="text-foreground">{lifecycle.guardsLive ? 'Live' : 'Waiting for first heartbeat'}</div>
                </div>
              </div>
              {installTracking.visibleInPortal && (
                <div className="rounded-md border border-border/50 bg-background/40 px-3 py-2 text-xs text-muted-foreground">
                  Hosted install path: <span className="text-foreground">{installTracking.label}</span>
                  <span className="mt-1 block">{installTracking.description}</span>
                  {installTracking.error && (
                    <span className="mt-1 block text-amber-400">{installTracking.error}</span>
                  )}
                </div>
              )}
            </div>
          )}
          <Progress value={progress} />
          <div className="space-y-2">
            <h3 className="text-base font-semibold">{currentStep.title}</h3>
            <p className="text-sm text-muted-foreground">{currentStep.description}</p>
            {currentStep.action}
            {currentStep.detail}
          </div>
        </div>
        <DialogFooter className="flex flex-col gap-2 sm:flex-row sm:justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setStepIndex((prev) => Math.max(prev - 1, 0))}
            disabled={stepIndex === 0}
          >
            Back
          </Button>
          <Button
            size="sm"
            onClick={() => {
              if (stepIndex === steps.length - 1) {
                onOpenChange(false);
              } else {
                setStepIndex((prev) => Math.min(prev + 1, steps.length - 1));
              }
            }}
          >
            {stepIndex === steps.length - 1 ? 'Done' : 'Next'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
